/**
 * Soft Fleet Dispatch — skill-matched multi-asset theatre with real OSM routes.
 * Soft≠Live · 127 only · no Live responder write · no hardware.
 *
 * Design (frontend-design skill, Ops vernacular):
 *   Subject — site command dispatch desk
 *   Signature — skill lattice (required vs covered) + OSM road lines
 *   Transit — walk/bike OSM · air corridor · static coverage
 */
import { useEffect, useRef, useState } from "react";
import { html } from "./ui.js";
import { isLocalHost } from "./pathseal.js";
import { CENTRE } from "./data.js";
import { haversineM } from "./soft-fleet-geo.js";
import {
  resolveSkillRoute,
  pickIncidentAnchorDevice,
  resolveP2PDestination,
} from "./soft-fleet-route.js";
import {
  SKILL,
  SKILL_ORDER,
  PROFILE_INK,
  buildSkillRoster,
  skillIncidentCatalog,
  matchSkills,
} from "./soft-fleet-skills.js";
import {
  SOFT_SCENES,
  glyphForSoftUnit,
  surveyedLlOf,
} from "./soft-fleet-scenes.js";
import {
  softDispatchFromMission,
  softDispatchesOf,
  softHumanRedirect,
  softHumanResumeAi,
  softHumanStopAll,
  softHumanStopDispatch,
  softHumanStopInZone,
  softMarkerPlaneOf,
  softRequestSnapshot,
} from "./soft-command-loop.js";

const TICK_MS = 700;
const SPAWN_EVERY_MS = 14000;

function stamp() {
  return new Date().toTimeString().slice(0, 8);
}

export function seedSoftFleet(centre) {
  const c = centre && centre.length === 2 ? centre : CENTRE;
  return {
    origin: c,
    assets: buildSkillRoster(c),
    incidents: [],
    missions: [],
    dispatches: [],
    ledger: [],
    evidence: [],
    ticks: 0,
    softDispatches: 0,
    templateI: 0,
    routeEngine: "osrm_osm+air",
    lastRouteErr: "",
    planning: false,
    scene: "perimeter",
    interactMode: "dispatch",
    selectedId: null,
    supervisor: { mode: "ai", paused: false, reason: "" },
  };
}

function pushLedger(state, kind, say, extra) {
  const row = {
    id: `L${state.ticks}-${state.ledger ? state.ledger.length : 0}`,
    ts: stamp(),
    kind,
    say,
    ...(extra || {}),
  };
  return [row, ...(state.ledger || [])].slice(0, 40);
}

/** Ledger + Soft evidence spine (Command OS audit fabric Soft). */
function _withAudit(state, kind, say, extra) {
  const ledger = pushLedger(state, kind, say, extra);
  const head = ledger[0];
  const evidence = [
    {
      id: head.id,
      ts: head.ts,
      kind: head.kind,
      say: head.say,
      unit: head.unit || null,
      mission: head.mission || null,
      device: head.device || null,
      soft: true,
    },
    ...(state.evidence || []),
  ].slice(0, 48);
  return { ...state, ledger, evidence };
}

/** Advance motion only (sync). */
export function tickMotion(state) {
  let { assets, incidents, missions, ledger, ticks } = state;
  assets = assets.map((a) => ({ ...a }));
  incidents = incidents.map((i) => ({ ...i }));
  missions = missions.map((m) => ({ ...m }));
  ticks += 1;
  const dt = TICK_MS / 1000;

  for (const a of assets) {
    if (!a.path || a.fixed || a.speed <= 0) continue;
    let remain = a.speed * dt;
    while (remain > 0 && a.pathI < a.path.length - 1) {
      const cur = a.ll;
      const nxt = a.path[a.pathI + 1];
      const seg = haversineM(cur, nxt);
      if (seg <= remain + 0.01) {
        a.ll = nxt.slice();
        a.pathI += 1;
        remain -= seg;
      } else {
        const t = remain / seg;
        a.ll = [cur[0] + (nxt[0] - cur[0]) * t, cur[1] + (nxt[1] - cur[1]) * t];
        remain = 0;
      }
    }
    a.etaS = Math.max(0, (a.etaS || 0) - dt);
    if (a.pathI >= a.path.length - 1) {
      const mid = a.missionId;
      a.path = null;
      a.pathI = 0;
      a.free = true;
      a.missionId = null;
      a.etaS = 0;
      a.routeMeta = null;
      const m = missions.find((x) => x.id === mid);
      if (m) {
        m.assigned = m.assigned.map((x) =>
          x.id === a.id ? { ...x, role: "on_scene" } : x,
        );
        if (m.assigned.every((x) => x.role === "on_scene" || x.role === "on_station")) {
          m.status = "soft_on_scene";
          const inc = incidents.find((i) => i.id === m.incidentId);
          if (inc) inc.status = "soft_contained";
          ledger = pushLedger(
            { ticks, ledger },
            "SOFT_ON_SCENE",
            `On scene · ${m.title} · skills sealed Soft`,
            { mission: mid },
          );
        }
      }
    }
  }

  return {
    ...state,
    assets,
    incidents,
    missions,
    ledger,
    ticks,
    dispatches: syncSoftDispatches({ ...state, assets, incidents, missions, ledger, ticks }),
  };
}

/**
 * Skill plan + real OSM/air routes · destinations = Soft edge devices (p2p).
 * Soft only · Soft≠Live.
 */
export async function planAndCommitDispatch(state) {
  const origin = state.origin || CENTRE;
  const catalog = skillIncidentCatalog(origin);
  const tpl = catalog[state.templateI % catalog.length];
  const templateI = state.templateI + 1;
  let assets = state.assets.map((a) => ({ ...a }));
  let ledger = state.ledger;
  let softDispatches = state.softDispatches;
  let lastRouteErr = "";

  /* Point-to-point: incident lives on a Soft edge device, not free-map fiction. */
  const anchor = pickIncidentAnchorDevice(assets, tpl, origin);
  if (!anchor || !anchor.ll) {
    ledger = pushLedger(
      state,
      "SOFT_DEFER",
      `No edge device endpoint for ${tpl.title} · p2p Soft`,
      {},
    );
    return {
      ...state,
      templateI,
      planning: false,
      ledger,
      lastRouteErr: "no_device_endpoint",
    };
  }

  const inc = {
    id: `INC-${state.ticks}-${templateI}`,
    title: tpl.title,
    needs: tpl.needs.slice(),
    severity: tpl.severity,
    ll: anchor.ll.slice(),
    place: `${anchor.label || anchor.id}${tpl.place ? " · " + tpl.place : ""}`,
    deviceId: anchor.id,
    status: "open",
    t0: state.ticks,
  };

  const match = matchSkills(inc, assets);
  if (!match.ok) {
    ledger = pushLedger(
      state,
      "SOFT_DEFER",
      `Skills open · missing ${match.missing} for ${inc.title}`,
      { incident: inc.id, device: anchor.id },
    );
    return {
      ...state,
      templateI,
      planning: false,
      ledger,
      lastRouteErr: "missing_skill:" + match.missing,
      incidents: [inc, ...state.incidents].slice(0, 8),
    };
  }

  const destMeta = {
    label: String(anchor.label || anchor.id),
    deviceId: String(anchor.id),
    mode: "device",
  };

  const resolved = [];
  let anyRouteFail = "";
  for (const member of match.team) {
    const a = assets.find((x) => x.id === member.assetId);
    if (!a) continue;
    if (member.kind === "on_station") {
      resolved.push({
        id: a.id,
        role: "on_station",
        etaS: 0,
        skills: member.skillsUsed,
        profile: "static",
        road: "coverage · " + destMeta.label,
        engine: "static",
        destDeviceId: destMeta.deviceId,
      });
      continue;
    }
    try {
      const leg = await resolveSkillRoute(a, anchor.ll, destMeta);
      a.free = false;
      a.missionId = null;
      a.path = leg.coords;
      a.pathI = 0;
      a.etaS = leg.durationS;
      a.routeMeta = {
        profile: leg.profile,
        engine: leg.engine,
        fromName: leg.fromName,
        toName: leg.toName || destMeta.label,
        distanceM: leg.distanceM,
        destDeviceId: destMeta.deviceId,
        destMode: "device",
      };
      a.color = PROFILE_INK[leg.profile] || a.color;
      const pathHow =
        leg.engine === "osrm_osm" || leg.engine === "osrm_at_pad"
          ? "OSM · " + (leg.profile || "walk")
          : leg.engine === "air_corridor"
            ? "air corridor"
            : leg.engine;
      resolved.push({
        id: a.id,
        role: "enroute",
        etaS: Math.round(leg.durationS),
        skills: member.skillsUsed,
        profile: leg.profile,
        road:
          [pathHow, leg.fromName, "→", destMeta.label].filter(Boolean).join(" ") ||
          pathHow,
        engine: leg.engine,
        distanceM: Math.round(leg.distanceM),
        destDeviceId: destMeta.deviceId,
      });
    } catch (e) {
      anyRouteFail = String((e && e.message) || e || "route_fail");
      ledger = pushLedger(
        { ticks: state.ticks, ledger },
        "SOFT_DEFER",
        `Route fail Soft · ${a.id} → ${destMeta.label} · ${anyRouteFail}`,
        { unit: a.id, device: destMeta.deviceId },
      );
    }
  }

  if (!resolved.length) {
    lastRouteErr = anyRouteFail || "route_fail";
    assets = state.assets.map((x) => ({ ...x }));
    return {
      ...state,
      templateI,
      planning: false,
      ledger,
      lastRouteErr,
      incidents: [{ ...inc, status: "route_fail" }, ...state.incidents].slice(0, 8),
    };
  }

  softDispatches += 1;
  const mid = `M-${state.ticks}-${templateI}`;
  for (const r of resolved) {
    if (r.role !== "enroute") continue;
    const a = assets.find((x) => x.id === r.id);
    if (a) a.missionId = mid;
  }
  const maxEta = Math.max(0, ...resolved.map((r) => r.etaS || 0));
  const mission = {
    id: mid,
    incidentId: inc.id,
    title: inc.title,
    place: inc.place,
    severity: inc.severity,
    needs: inc.needs.slice(),
    maxEta: Math.round(maxEta),
    assigned: resolved,
    status: "enroute",
    soft: true,
    destDeviceId: destMeta.deviceId,
    destMode: "device",
    skillCover: skillCoverView(inc.needs, resolved, assets),
  };
  inc.status = "dispatched_soft";
  const skillLine = (inc.needs || []).join("+");
  const names = resolved.map((x) => x.id).join(", ");
  const roads = resolved
    .filter((x) => x.role === "enroute")
    .map((x) => x.profile + ":" + (x.road || x.engine))
    .slice(0, 2)
    .join(" · ");
  ledger = pushLedger(
    state,
    "SOFT_DISPATCH",
    `P2P → ${destMeta.label} · ${skillLine} · η ${Math.round(maxEta)}s · ${names} · ${roads || "on station"} · Soft≠Live`,
    { mission: mid, severity: inc.severity, device: destMeta.deviceId },
  );

  const next = {
    ...state,
    assets,
    templateI,
    softDispatches,
    planning: false,
    lastRouteErr: anyRouteFail || "",
    ledger,
    evidence: evidenceFromLedger(state, ledger),
    incidents: [inc, ...state.incidents].slice(0, 8),
    missions: [mission, ...state.missions].slice(0, 12),
  };
  next.dispatches = syncSoftDispatches(next);
  return next;
}

function skillCoverView(needs, assigned, assets) {
  const covered = new Set();
  for (const a of assigned) {
    (a.skills || []).forEach((s) => covered.add(s));
  }
  // also static coverage skills already in assigned.on_station
  for (const n of needs || []) {
    if (!covered.has(n)) {
      // may already be satisfied by multi-skill asset
      for (const a of assigned) {
        const asset = assets.find((x) => x.id === a.id);
        if (asset && asset.skills && asset.skills.includes(n)) covered.add(n);
      }
    }
  }
  return SKILL_ORDER.map((id) => ({
    id,
    label: (SKILL[id] && SKILL[id].label) || id,
    needed: (needs || []).includes(id),
    covered: covered.has(id) && (needs || []).includes(id),
  }));
}

/** Materialise first-class Soft Dispatch objects from missions. */
function syncSoftDispatches(state) {
  if (!state) return [];
  return (state.missions || [])
    .slice(0, 16)
    .map((m) => softDispatchFromMission(m, state.assets || [], state));
}

/** Append Soft evidence spine from latest ledger head. */
function evidenceFromLedger(state, ledger) {
  const head = ledger && ledger[0];
  if (!head) return state.evidence || [];
  return [
    {
      id: head.id,
      ts: head.ts,
      kind: head.kind,
      say: head.say,
      unit: head.unit || null,
      mission: head.mission || null,
      device: head.device || null,
      soft: true,
    },
    ...(state.evidence || []),
  ].slice(0, 48);
}

/** Apply planned dispatch onto current motion state (avoid position rewind). */
export function mergePlanResult(cur, planned) {
  if (!planned) return cur;
  if (!cur) return planned;
  const newMissions = (planned.missions || []).filter(
    (m) => !(cur.missions || []).some((c) => c.id === m.id),
  );
  const _newIncIds = new Set((planned.incidents || []).map((i) => i.id));
  const oldIncIds = new Set((cur.incidents || []).map((i) => i.id));
  const addedInc = (planned.incidents || []).filter((i) => !oldIncIds.has(i.id));

  if (!newMissions.length) {
    return {
      ...cur,
      templateI: planned.templateI,
      planning: false,
      lastRouteErr: planned.lastRouteErr || "",
      ledger: planned.ledger || cur.ledger,
      incidents: addedInc.length
        ? [...addedInc, ...cur.incidents].slice(0, 8)
        : cur.incidents,
      softDispatches: planned.softDispatches,
    };
  }

  const byId = Object.fromEntries((planned.assets || []).map((a) => [a.id, a]));
  const assets = (cur.assets || []).map((a) => {
    const p = byId[a.id];
    if (!p || !p.missionId || !p.path) return a;
    if (!newMissions.some((m) => m.id === p.missionId)) return a;
    if (!a.free && a.missionId) return a;
    return {
      ...a,
      free: false,
      missionId: p.missionId,
      path: p.path,
      pathI: 0,
      etaS: p.etaS,
      routeMeta: p.routeMeta,
      color: p.color || a.color,
    };
  });

  return {
    ...cur,
    assets,
    missions: [...newMissions, ...(cur.missions || [])].slice(0, 12),
    incidents: addedInc.length
      ? [...addedInc, ...(cur.incidents || [])].slice(0, 8)
      : cur.incidents,
    ledger: planned.ledger || cur.ledger,
    evidence: planned.evidence || cur.evidence,
    softDispatches: planned.softDispatches,
    templateI: planned.templateI,
    planning: false,
    lastRouteErr: planned.lastRouteErr || "",
    dispatches: syncSoftDispatches({
      ...cur,
      assets,
      missions: [...newMissions, ...(cur.missions || [])].slice(0, 12),
      softDispatches: planned.softDispatches,
    }),
  };
}

/** Operator: Skill Weave scene (icons + boost Soft only). */
export function setSoftScene(state, sceneId) {
  if (!state) return state;
  const id = SOFT_SCENES[sceneId] ? sceneId : "perimeter";
  const ledger = pushLedger(
    state,
    "SOFT_SCENE",
    `Scene · ${SOFT_SCENES[id].label} · Skill Weave Soft≠Live`,
    { scene: id },
  );
  return { ...state, scene: id, ledger };
}

export function setSoftInteractMode(state, mode) {
  if (!state) return state;
  const m = mode === "place" || mode === "bearing" ? mode : "dispatch";
  return { ...state, interactMode: m };
}

export function selectSoftUnit(state, unitId) {
  if (!state) return state;
  return { ...state, selectedId: unitId ? String(unitId) : null };
}

/** Place / re-survey Soft pin Soft theatre only. */
export function placeSoftUnit(state, unitId, ll, meta) {
  if (!state || !unitId || !ll || ll.length < 2) return state;
  const lng = Number(ll[0]);
  const lat = Number(ll[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return state;
  let assets = state.assets.map((a) => ({ ...a }));
  const a = assets.find((x) => x.id === unitId);
  if (!a) return state;
  a.ll = [lng, lat];
  a.home = [lng, lat];
  if (a.fixed || a.routeProfile === "static" || !(a.speed > 0)) {
    a.path = null;
    a.pathI = 0;
    a.free = true;
    a.missionId = null;
    a.etaS = 0;
  }
  if (meta && meta.surveyed) a.surveyed = true;
  const ledger = pushLedger(
    state,
    "SOFT_PLACE",
    `Placed ${a.label || a.id}${meta && meta.surveyed ? " · surveyed edge pin" : ""} · Soft`,
    { unit: a.id },
  );
  return { ...state, assets, ledger, selectedId: a.id };
}

/**
 * Human dispatch Soft unit toward map drop.
 * Destination snaps to Soft edge device (p2p) or nearby recon pad · Soft≠Live.
 */
/**
 * @param {object} [opts] preferDeviceId / preferLl · pixel-accurate screen pick
 */
export async function dispatchSoftUnitTo(state, unitId, targetLl, opts) {
  if (!state || !unitId || !targetLl || targetLl.length < 2) return state;
  let assets = state.assets.map((a) => ({ ...a }));
  const a = assets.find((x) => x.id === unitId);
  if (!a) return state;
  if (a.fixed || a.routeProfile === "static" || !(a.speed > 0)) {
    return {
      ...state,
      ledger: pushLedger(
        state,
        "SOFT_DEFER",
        `${a.label || a.id} cannot move · fixed/static Soft`,
        { unit: a.id },
      ),
    };
  }

  const dest = resolveP2PDestination(assets, targetLl, a, opts);
  if (dest.mode === "none" || !dest.ll) {
    return {
      ...state,
      ledger: pushLedger(
        state,
        "SOFT_DEFER",
        "Aim on a Soft device pin (≤72m) or nearby recon pad · Soft",
        { unit: a.id },
      ),
    };
  }

  try {
    const destMeta = {
      label: dest.label,
      deviceId: dest.deviceId,
      mode: dest.mode,
    };
    const leg = await resolveSkillRoute(a, dest.ll, destMeta);
    const mid = `M-OP-${state.ticks}-${a.id}`;
    a.free = false;
    a.missionId = mid;
    a.path = leg.coords;
    a.pathI = 0;
    a.etaS = leg.durationS;
    a.routeMeta = {
      profile: leg.profile,
      engine: leg.engine,
      fromName: leg.fromName,
      toName: leg.toName || dest.label,
      distanceM: leg.distanceM,
      operator: true,
      destDeviceId: dest.deviceId,
      destMode: dest.mode,
    };
    a.color = PROFILE_INK[leg.profile] || a.color;
    const pathHow =
      leg.engine === "osrm_osm" || leg.engine === "osrm_at_pad"
        ? "OSM p2p"
        : leg.engine === "air_corridor"
          ? "air p2p"
          : leg.engine;
    const place =
      dest.mode === "recon" ? "nearby recon Soft" : `P2P · ${dest.label}`;
    const mission = {
      id: mid,
      incidentId: null,
      title: dest.mode === "recon" ? "Nearby recon" : "Operator p2p",
      place,
      severity: "op",
      needs: (a.skills || []).slice(0, 3),
      maxEta: Math.round(leg.durationS),
      assigned: [
        {
          id: a.id,
          role: "enroute",
          etaS: Math.round(leg.durationS),
          skills: a.skills || [],
          profile: leg.profile,
          road: `${pathHow} → ${dest.label}`,
          engine: leg.engine,
          destDeviceId: dest.deviceId,
        },
      ],
      status: "enroute",
      soft: true,
      destDeviceId: dest.deviceId,
      destMode: dest.mode,
      skillCover: [],
    };
    const ledger = pushLedger(
      state,
      "SOFT_DISPATCH",
      `Operator · ${a.label || a.id} → ${place} · η ${Math.round(leg.durationS)}s · ${pathHow} Soft≠Live`,
      { unit: a.id, mission: mid, device: dest.deviceId, destMode: dest.mode },
    );
    const next = {
      ...state,
      assets,
      softDispatches: (state.softDispatches || 0) + 1,
      missions: [mission, ...(state.missions || [])].slice(0, 12),
      ledger,
      evidence: evidenceFromLedger(state, ledger),
      selectedId: a.id,
      planning: false,
    };
    next.dispatches = syncSoftDispatches(next);
    return next;
  } catch (e) {
    return {
      ...state,
      lastRouteErr: String((e && e.message) || e || "route_fail"),
      ledger: pushLedger(
        state,
        "SOFT_DEFER",
        `OSM/air route fail Soft · ${a.id} → ${dest.label}`,
        { unit: a.id, device: dest.deviceId },
      ),
    };
  }
}

/** Swap two Soft people (missions + free). Soft≠Live. */
export function reassignSoftPerson(state, fromId, toId) {
  if (!state || !fromId || !toId || fromId === toId) return state;
  let assets = state.assets.map((a) => ({ ...a }));
  const a = assets.find((x) => x.id === fromId);
  const b = assets.find((x) => x.id === toId);
  if (!a || !b) return state;
  if (a.kind !== "person" || b.kind !== "person") {
    return {
      ...state,
      ledger: pushLedger(state, "SOFT_DEFER", "Reassign Soft · people only", {}),
    };
  }
  const tmp = {
    free: a.free,
    missionId: a.missionId,
    path: a.path,
    pathI: a.pathI,
    etaS: a.etaS,
    routeMeta: a.routeMeta,
    ll: a.ll.slice(),
  };
  a.free = b.free;
  a.missionId = b.missionId;
  a.path = b.path;
  a.pathI = b.pathI;
  a.etaS = b.etaS;
  a.routeMeta = b.routeMeta;
  a.ll = b.ll.slice();
  b.free = tmp.free;
  b.missionId = tmp.missionId;
  b.path = tmp.path;
  b.pathI = tmp.pathI;
  b.etaS = tmp.etaS;
  b.routeMeta = tmp.routeMeta;
  b.ll = tmp.ll;
  const ledger = pushLedger(
    state,
    "SOFT_REASSIGN",
    `People swap · ${a.label} ⇄ ${b.label} · Soft≠Live`,
    { from: a.id, to: b.id },
  );
  return { ...state, assets, ledger };
}

export function setSoftBearing(state, unitId, deg) {
  if (!state || !unitId) return state;
  const brg = ((Number(deg) % 360) + 360) % 360;
  if (!Number.isFinite(brg)) return state;
  const assets = state.assets.map((a) =>
    a.id === unitId ? { ...a, bearing: brg } : a,
  );
  return { ...state, assets, selectedId: unitId };
}

/**
 * Mirror Live surveyed positions onto Soft theatre pins.
 * Soft only — never writes Live hardware.
 */
export function snapSoftToSurvey(state, liveDevices) {
  if (!state || !Array.isArray(liveDevices) || !liveDevices.length) return state;
  let assets = state.assets.map((a) => ({ ...a }));
  let n = 0;
  for (const d of liveDevices) {
    const ll = surveyedLlOf(d);
    if (!ll) continue;
    const id = String((d && (d.id || d.device_id)) || "");
    if (!id) continue;
    let hit = assets.find((x) => x.id === id);
    if (!hit) {
      const label = String((d && (d.label || d.name || d.id)) || id);
      hit = assets.find(
        (x) =>
          x.kind === "device" &&
          (String(x.label).toLowerCase() === label.toLowerCase() ||
            String(x.id).toLowerCase() === id.toLowerCase()),
      );
    }
    if (!hit) continue;
    hit.ll = ll.slice();
    hit.home = ll.slice();
    hit.surveyed = true;
    hit.path = null;
    hit.pathI = 0;
    n += 1;
  }
  if (!n) return state;
  const ledger = pushLedger(
    state,
    "SOFT_SURVEY",
    `Snap ${n} pins from Live surveyed · Soft theatre only · never other tenants`,
    { n },
  );
  return { ...state, assets, ledger };
}

export function softFleetGeoJSON(state) {
  if (!state) return { routes: emptyFC(), pins: emptyFC(), incidents: emptyFC() };
  const routes = [];
  const pins = [];
  const incs = [];
  const scene = state.scene || "perimeter";
  for (const a of state.assets || []) {
    const profile = (a.routeMeta && a.routeMeta.profile) || a.routeProfile || "walk";
    pins.push({
      type: "Feature",
      properties: {
        id: a.id,
        kind: a.kind,
        tier: a.tier,
        label: a.label,
        color: a.color || PROFILE_INK[profile] || "#8FA39E",
        free: a.free ? 1 : 0,
        eta: Math.round(a.etaS || 0),
        skills: (a.skills || []).join(","),
        profile,
        scene,
        fixed: a.fixed ? 1 : 0,
        surveyed: a.surveyed ? 1 : 0,
        bearing: Number.isFinite(a.bearing) ? a.bearing : undefined,
        plane: softMarkerPlaneOf(a),
        glyph: glyphForSoftUnit({
          id: a.id,
          kind: a.kind,
          skills: a.skills,
          scene,
          glyph: a.glyph,
        }),
        destDeviceId: (a.routeMeta && a.routeMeta.destDeviceId) || "",
        useHow: a.free
          ? "available"
          : a.etaS > 0
            ? "enroute"
            : a.missionId
              ? "on_scene"
              : "available",
        missionTitle: a.missionId || "",
      },
      geometry: { type: "Point", coordinates: a.ll },
    });
    if (a.path && a.path.length > 1) {
      routes.push({
        type: "Feature",
        properties: {
          id: a.id,
          kind: a.kind,
          color: PROFILE_INK[profile] || a.color,
          profile,
          engine: (a.routeMeta && a.routeMeta.engine) || "",
          destDeviceId: (a.routeMeta && a.routeMeta.destDeviceId) || "",
        },
        geometry: { type: "LineString", coordinates: a.path.slice(a.pathI) },
      });
    }
  }
  for (const i of state.incidents || []) {
    if (i.status === "soft_contained") continue;
    incs.push({
      type: "Feature",
      properties: {
        id: i.id,
        title: i.title,
        severity: i.severity,
        status: i.status,
        place: i.place || "",
        needs: (i.needs || []).join(","),
      },
      geometry: { type: "Point", coordinates: i.ll },
    });
  }
  return {
    routes: { type: "FeatureCollection", features: routes },
    pins: { type: "FeatureCollection", features: pins },
    incidents: { type: "FeatureCollection", features: incs },
  };
}

function emptyFC() {
  return { type: "FeatureCollection", features: [] };
}

export function useSoftFleetSim(centre) {
  const local = isLocalHost();
  const [enabled] = useState(local);
  const [state, setState] = useState(() => (local ? seedSoftFleet(centre || CENTRE) : null));
  const centreRef = useRef(centre);
  const planningRef = useRef(false);
  const lastSpawnRef = useRef(0);
  centreRef.current = centre;

  useEffect(() => {
    if (!local) return;
    setState((s) => {
      if (!s) return seedSoftFleet(centre || CENTRE);
      const d = haversineM(s.origin, centre || CENTRE);
      if (d > 2500) {
        lastSpawnRef.current = 0;
        return seedSoftFleet(centre || CENTRE);
      }
      return s;
    });
  }, [local, centre && centre[0], centre && centre[1]]);

  /* Motion clock. */
  useEffect(() => {
    if (!local || !enabled) return undefined;
    const id = setInterval(() => {
      setState((s) => (s ? tickMotion(s) : seedSoftFleet(centreRef.current || CENTRE)));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [local, enabled]);

  /* Skill dispatch + real OSM/air routes (async, merged into motion state). */
  const pausedRef = useRef(false);
  pausedRef.current = !!(state && state.supervisor && state.supervisor.paused);
  useEffect(() => {
    if (!local || !enabled) return undefined;
    let dead = false;
    const run = () => {
      if (dead || planningRef.current || pausedRef.current) return;
      const now = Date.now();
      if (lastSpawnRef.current && now - lastSpawnRef.current < SPAWN_EVERY_MS) return;
      planningRef.current = true;
      lastSpawnRef.current = now;
      setState((s) => {
        if (!s) {
          planningRef.current = false;
          return seedSoftFleet(centreRef.current || CENTRE);
        }
        if (s.supervisor && s.supervisor.paused) {
          planningRef.current = false;
          return s;
        }
        const snap = { ...s, assets: s.assets.map((a) => ({ ...a })), planning: true };
        planAndCommitDispatch(snap)
          .then((planned) => {
            if (!dead) setState((cur) => mergePlanResult(cur, planned));
          })
          .catch((e) => {
            if (!dead) {
              setState((cur) =>
                cur
                  ? {
                      ...cur,
                      planning: false,
                      lastRouteErr: String((e && e.message) || e),
                      ledger: pushLedger(
                        cur,
                        "SOFT_DEFER",
                        "Dispatch planner fault Soft · " + String((e && e.message) || e),
                      ),
                    }
                  : cur,
              );
            }
          })
          .finally(() => {
            planningRef.current = false;
          });
        return { ...s, planning: true };
      });
    };
    const warm = setTimeout(run, 900);
    const id = setInterval(run, 2500);
    return () => {
      dead = true;
      clearTimeout(warm);
      clearInterval(id);
    };
  }, [local, enabled]);

  return {
    enabled: local && enabled,
    state,
    geo: softFleetGeoJSON(state),
    local,
    setScene: (id) => setState((s) => (s ? setSoftScene(s, id) : s)),
    setInteractMode: (m) => setState((s) => (s ? setSoftInteractMode(s, m) : s)),
    selectUnit: (id) => setState((s) => (s ? selectSoftUnit(s, id) : s)),
    placeUnit: (id, ll, meta) => setState((s) => (s ? placeSoftUnit(s, id, ll, meta) : s)),
    dispatchTo: async (id, ll, opts) => {
      const snap = state;
      if (!snap) throw new Error("soft_off");
      const next = await dispatchSoftUnitTo(snap, id, ll, opts);
      setState(next);
      const top = next && next.ledger && next.ledger[0];
      if (top && top.kind === "SOFT_DEFER") {
        throw new Error(top.say || "dispatch_defer");
      }
      return next;
    },
    reassign: (fromId, toId) =>
      setState((s) => (s ? reassignSoftPerson(s, fromId, toId) : s)),
    setBearing: (id, deg) => setState((s) => (s ? setSoftBearing(s, id, deg) : s)),
    snapSurvey: (devices) => setState((s) => (s ? snapSoftToSurvey(s, devices) : s)),
    humanStopAll: (reason) => setState((s) => (s ? softHumanStopAll(s, reason) : s)),
    humanStopInZone: (origin, radiusM, reason) =>
      setState((s) => (s ? softHumanStopInZone(s, origin, radiusM, reason) : s)),
    humanResumeAi: () => setState((s) => (s ? softHumanResumeAi(s) : s)),
    humanStopDispatch: (id) => setState((s) => (s ? softHumanStopDispatch(s, id) : s)),
    humanRedirect: (id, obj) => setState((s) => (s ? softHumanRedirect(s, id, obj) : s)),
    requestSnapshot: (camId) => setState((s) => (s ? softRequestSnapshot(s, camId) : s)),
    dispatchesOf: () => softDispatchesOf(state),
  };
}

function SkillLattice({ needs, cover }) {
  const rows = SKILL_ORDER.map((id) => {
    const meta = SKILL[id];
    const needed = (needs || []).includes(id);
    const covered =
      cover && cover.find((c) => c.id === id)
        ? cover.find((c) => c.id === id).covered
        : false;
    return { id, label: meta.label, needed, covered: needed && covered };
  });
  return html`
    <div className="sflattice" role="list" aria-label="Skill lattice">
      ${rows.map((r) => html`
        <span
          key=${r.id}
          role="listitem"
          className="sflattice__cell"
          data-needed=${r.needed ? "1" : "0"}
          data-cover=${r.covered ? "1" : "0"}>
          <b>${r.label}</b>
        </span>`)}
    </div>`;
}

export function SoftFleetPanel({ fleet }) {
  if (!fleet || !fleet.enabled || !fleet.state) {
    return html`
      <section className="softfleet softfleet--off" data-cmc-soft-fleet="1" data-cmc-class-c="1"
        data-local=${typeof location !== "undefined" && (location.hostname === "127.0.0.1" || location.hostname === "localhost") ? "1" : "0"}
        aria-label="Local practice dispatch">
        <p className="softfleet__banner">Local practice only — never production dispatch</p>
        <p className="note">Multi-asset practice routing never invents live dispatch on production hosts.</p>
      </section>`;
  }
  const s = fleet.state;
  const missions = (s.missions || []).slice(0, 3);
  const lead = missions[0];
  const ledger = (s.ledger || []).slice(0, 8);
  const byKind = { person: 0, device: 0, drone: 0, robot: 0 };
  for (const a of s.assets || []) byKind[a.kind] = (byKind[a.kind] || 0) + 1;
  const localHost = typeof location !== "undefined"
    && (location.hostname === "127.0.0.1" || location.hostname === "localhost" || location.hostname === "[::1]");

  return html`
    <section className="softfleet" aria-label="Local practice skill dispatch" data-plan=${s.planning ? "1" : "0"}
      data-cmc-class-c="1" data-cmc-soft-fleet="1" data-local=${localHost ? "1" : "0"}>
      <div className="softfleet__banner">
        Local practice · skill match · never live dispatch
      </div>
      <div className="softfleet__head">
        <div>
          <p className="softfleet__eyebrow">Skill lattice · physical routing</p>
          <h3>Dispatch by skill, travel on real roads</h3>
          <p className="softfleet__lede">
            Each job names skills. Station cams/sirens cover first. Foot and UGV take OpenStreetMap
            legs (OSRM). Drones fly an air corridor. Soft≠Live.
          </p>
        </div>
        <span className="softfleet__lamp" data-run=${s.planning ? "0" : "1"}>
          ${s.planning ? "routing…" : "live Soft"}
        </span>
      </div>

      ${lead
        ? html`
            <div className="softfleet__now">
              <header>Current job · skill seal</header>
              <div className="softfleet__mission" data-sev=${lead.severity} data-st=${lead.status}>
                <div className="softfleet__mission-top">
                  <b>${lead.title}</b>
                  <em>η max ${lead.maxEta}s</em>
                </div>
                ${lead.place ? html`<span className="softfleet__place">${lead.place}</span>` : null}
                <${SkillLattice} needs=${lead.needs} cover=${lead.skillCover}/>
                <ul className="softfleet__assign">
                  ${(lead.assigned || []).map((x) => html`
                    <li key=${x.id} data-role=${x.role}>
                      <b>${x.id}</b>
                      <em>${x.profile || "—"} · ${x.role}</em>
                      <span>${x.road || (x.skills || []).join(" · ")}</span>
                      ${x.etaS ? html`<i>${x.etaS}s · ${x.distanceM || "?"}m</i>` : null}
                    </li>`)}
                </ul>
              </div>
            </div>`
        : html`
            <div className="softfleet__now">
              <header>Skill lattice (waiting job)</header>
              <${SkillLattice} needs=${["observe", "patrol", "alert"]} cover=${[]}/>
              <p className="note">First Soft incident routing on OSM…</p>
            </div>`}

      <div className="softfleet__kpis">
        <span><em>dispatches</em><b>${s.softDispatches}</b></span>
        <span><em>engine</em><b>${s.routeEngine || "osrm"}</b></span>
        <span><em>people</em><b>${byKind.person}</b></span>
        <span><em>devices</em><b>${byKind.device}</b></span>
        <span><em>UAV</em><b>${byKind.drone}</b></span>
        <span><em>UGV</em><b>${byKind.robot}</b></span>
      </div>
      ${s.lastRouteErr
        ? html`<p className="softfleet__err">Route Soft fault · ${s.lastRouteErr}</p>`
        : null}

      <div className="softfleet__roster" aria-label="Fleet by skill">
        ${(s.assets || []).map((a) => html`
          <div key=${a.id} className="softfleet__chip" data-kind=${a.kind} data-free=${a.free ? "1" : "0"}
            style=${{ "--ac": a.color }}>
            <div className="softfleet__chip-top">
              <b>${a.label}</b>
              <em>${a.routeProfile}</em>
            </div>
            <span className="softfleet__skills">${(a.skills || []).join(" · ")}</span>
            <i>${a.free ? "idle" : "enroute " + Math.round(a.etaS || 0) + "s"}</i>
          </div>`)}
      </div>

      <div className="softfleet__feed">
        <header>Soft dispatch ledger</header>
        ${ledger.map((r) => html`
          <div key=${r.id} className="softfleet__row" data-kind=${r.kind}>
            <time>${r.ts}</time>
            <b>${r.kind}</b>
            <span>${r.say}</span>
          </div>`)}
      </div>
    </section>`;
}
